import { CommonModule } from '@angular/common';
import { Component, Inject, Input, SecurityContext, Signal, WritableSignal, computed, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import {
  CampaignService,
  GetterCampaignData,
  RecipientCampaignService,
  RecipientCampaignStruct,
} from '@vendasta/campaigns';
import { ActivityInterface, GetMultiCrmObjectResponseInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n';
import {
  CrmFieldService,
  CrmObjectService,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
} from '../../../shared-services';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { CrmUnknownUserAvatarComponent } from '../../unknown-user-avatar/unknown-user-avatar.component';
import { ActivityExtended } from '../timeline/timeline.service';

@Component({
  selector: 'crm-activity-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  providers: [RxState],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    CollapsibleSectionComponent,
    MatCardModule,
    GalaxyAvatarModule,
    CrmUnknownUserAvatarComponent,
    MatIconModule,
    GalaxyPipesModule,
  ],
  standalone: true,
})
export class CrmActivityEmailComponent {
  unknownNamePlaceholder = this.translateService.instant('CONTACT.ACTIVITIES.UNKNOWN_NAME');
  private _activity: WritableSignal<ActivityExtended> = signal({});
  private _associatedObjectId: WritableSignal<string> = signal('');
  private _objectType: WritableSignal<ObjectType> = signal('Contact');

  @Input({ required: true }) set activity(activity: ActivityExtended) {
    this._activity.set(activity);
  }

  get activity() {
    return this._activity();
  }

  @Input() set objectType(objectType: ObjectType) {
    this._objectType.set(objectType);
  }

  get objectType() {
    return this._objectType();
  }

  @Input() set associatedObjectId(crmObjectId: string) {
    this._associatedObjectId.set(crmObjectId);
  }

  get associatedObjectId() {
    return this._associatedObjectId();
  }

  private _showAvatar = true;
  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }

  get showAvatar() {
    return this._showAvatar;
  }

  dateFormat = DateFormat.medium;

  associatedCrmObjectName$ = toObservable(this._activity).pipe(switchMap(() => this.getAssociatedObjectName()));
  associatedCrmObjectName = toSignal(this.associatedCrmObjectName$);

  contactEmails$ = toObservable(this._activity).pipe(switchMap((activity) => this.getContactEmails(activity)));
  contactEmails = toSignal(this.contactEmails$);

  campaignId$ = toObservable(this._activity).pipe(switchMap((activity) => this.getCampaignId(activity)));
  campaignId = toSignal(this.campaignId$);

  campaignName$ = toObservable(this._activity).pipe(switchMap((activity) => this.getCampaign(activity)));
  campaignName = toSignal(this.campaignName$);

  accountGroupId$ = toObservable(this._activity).pipe(switchMap((activity) => this.getAccountGroupId(activity)));
  accountGroupId = toSignal(this.accountGroupId$);

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      emailBody:
        this.sanitizer.sanitize(
          SecurityContext.HTML,
          this.getStringFieldData(this.activity, StandardIds.ActivityEmailBody),
        ) || '',

      emailSubject: this.getStringFieldData(this.activity, StandardIds.ActivityEmailSubject),
      emailStatus: this.getStringFieldData(this.activity, StandardIds.ActivityEmailStatus),
      emailDirection: this.getStringFieldData(this.activity, StandardIds.ActivityEmailDirection)?.toLowerCase(),

      emailType: this.getStringFieldData(this.activity, StandardIds.ActivityEmailType),
      emailHeaders: this.getStringFieldData(this.activity, StandardIds.ActivityEmailHeaders) || '',
      sourceName: this.getStringFieldData(this.activity, StandardIds.ActivitySourceName),
      effectiveDate: this.getDateFieldData(this.activity, SystemFieldIds.ActivityEffectiveDate),
    };
  });

  ownerName = computed(() => {
    const owner = this.activity?.ownerUser;
    if (owner) {
      return `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim();
    }
    return this.unknownNamePlaceholder;
  });

  isCampaign = computed(() => {
    const { emailType } = this.cardInfo();
    return emailType === 'marketing_campaign_email';
  });

  headerText = computed(() => {
    const { emailType, emailDirection, emailStatus } = this.cardInfo();
    let baseText = '';
    let nameText = '';
    if (emailType === 'marketing_campaign_email') {
      baseText = this.translateService.instant('ACTIVITY_TYPES.CAMPAIGN') + ' ';
      nameText = this.associatedCrmObjectName() || '';
    } else {
      baseText = this.translateService.instant('ACTIVITY_TYPES.EMAIL') + ' ';
      nameText = this.ownerName();
    }

    let verbText = '';
    if (emailDirection === 'outbound') {
      verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.OUTBOUND') + ' ';
    } else if (emailDirection === 'forwarded') {
      verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.FORWARDED') + ' ';
    } else if (emailDirection === 'inbound') {
      verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.INBOUND') + ' ';
    } else {
      switch (emailStatus) {
        case 'bounced':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.BOUNCED') + ' ';
          break;
        case 'clicked':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.CLICKED') + ' ';
          break;
        case 'deferred':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.DEFERRED') + ' ';
          break;
        case 'delivered':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.DELIVERED') + ' ';
          break;
        case 'dropped':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.DROPPED') + ' ';
          break;
        case 'failed':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.FAILED') + ' ';
          break;
        case 'opened':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.OPENED') + ' ';
          break;
        case 'processed':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.PROCESSED') + ' ';
          break;
        case 'received':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.RECEIVED') + ' ';
          break;
        case 'resubscribed':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.RESUBSCRIBED') + ' ';
          break;
        case 'sent':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.SENT') + ' ';
          break;
        case 'spamreport':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.SPAM') + ' ';
          break;
        case 'unsubscribed':
          verbText = this.translateService.instant('ACTIVITY.EMAIL_TITLE.UNSUBSCRIBED') + ' ';
          break;
      }
    }
    return `${baseText}${verbText}${nameText}`.trim();
  });

  campaignLink = computed(() => {
    if (this.useBusinessCenterCampaignLink()) {
      return `/account/location/${this.accountGroupId()}/campaigns/${this.campaignId()}/details`;
    } else {
      return `/marketing/campaign/details/${this.campaignId()}`;
    }
  });

  useBusinessCenterCampaignLink: Signal<boolean | undefined>;

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly fieldService: CrmFieldService,
    private readonly campaignService: CampaignService,
    private readonly recipientCampaignService: RecipientCampaignService,
    private readonly crmObjectService: CrmObjectService,
    private readonly translateService: TranslateService,
    private readonly sanitizer: DomSanitizer,
  ) {
    this.useBusinessCenterCampaignLink = toSignal(
      config.parentNamespace$.pipe(
        map((parentNamespace) => {
          return parentNamespace !== '';
        }),
      ),
    );
  }

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getCampaignName(id: string): Observable<string> {
    if (id !== '') {
      return this.campaignService.get(id).pipe(
        map((campaignData: GetterCampaignData) => {
          return campaignData.name;
        }),
      );
    }
    return of('');
  }

  private getDateFieldData(activity: ActivityInterface, fieldId: string): Date | null {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.dateValue ?? null;
  }

  private getCampaignId(activity: ActivityInterface): Observable<string> {
    const recipientId = this.getStringFieldData(activity, StandardIds.ActivityCampaignID);
    if (recipientId !== '') {
      return this.recipientCampaignService.getRecipientCampaign(recipientId).pipe(
        map((recipientCampaign: RecipientCampaignStruct) => {
          return recipientCampaign.campaignId;
        }),
      );
    }
    return of('');
  }

  private getAccountGroupId(activity: ActivityInterface): Observable<string> {
    const recipientId = this.getStringFieldData(activity, StandardIds.ActivityCampaignID);
    if (recipientId !== '') {
      return this.recipientCampaignService.getRecipientCampaign(recipientId).pipe(
        map((recipientCampaign: RecipientCampaignStruct) => {
          const parts = recipientCampaign.partnerId.split('/');
          if (parts.length > 1) {
            return parts[1];
          }
          return '';
        }),
      );
    }

    return of('');
  }

  private getCampaign(activity: ActivityInterface): Observable<string> {
    const campaignID$ = this.getCampaignId(activity);
    return campaignID$.pipe(
      switchMap((campaignId: string) => {
        return this.getCampaignName(campaignId);
      }),
    );
  }

  getAssociatedObjectName(): Observable<string> {
    if (this.objectType === 'Contact') {
      return this.crmObjectService.getMultiObject('Contact', [this.associatedObjectId]).pipe(
        map((data: GetMultiCrmObjectResponseInterface) => {
          const crmObject = data?.crmObjects[0];
          const firstName =
            this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.FirstName)?.stringValue || '';
          const lastName =
            this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.LastName)?.stringValue || '';
          return `${firstName} ${lastName}` ?? '';
        }),
      );
    }
    if (this.objectType === 'Company') {
      return this.crmObjectService.getMultiObject('Company', [this.associatedObjectId]).pipe(
        map((data: GetMultiCrmObjectResponseInterface) => {
          const crmObject = data?.crmObjects[0];
          const companyName = this.fieldService.getFieldValueFromCrmObject(
            crmObject,
            StandardIds.CompanyName,
          )?.stringValue;
          return `${companyName}` ?? '';
        }),
      );
    }
    return of('');
  }

  private getContactEmails(activity: ActivityInterface): Observable<(string | undefined)[]> {
    const crmObjectIds: string[] = [];
    activity.associations?.forEach((association) => {
      if (association?.crmObjectType === 'Contact' && association?.crmObjectId) {
        crmObjectIds.push(association.crmObjectId);
      }
    });
    if (crmObjectIds.length > 0) {
      const contactDetails$ = this.crmObjectService.getMultiObject('Contact', crmObjectIds);
      return contactDetails$.pipe(
        map((resp) => {
          return resp?.crmObjects
            ? resp?.crmObjects?.map(
                (contact) =>
                  this.fieldService.getFieldValueFromCrmObject(contact, StandardExternalIds.Email)?.stringValue,
              )
            : [];
        }),
      );
    }
    return of([]);
  }
}

interface ActivityCardInformation {
  sourceName: string;
  emailBody: string;
  emailDirection: string;
  emailStatus: string;
  emailSubject: string;
  emailType: string;
  emailHeaders: string;
}
